import { useState } from 'react';

import { Grid, XStack, YStack } from '@/components/ui/layout/layout';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip } from '@/components/ui/Tooltip';
import { cn } from '@/lib/utils';

import { useCreditBalanceQuery } from './subscription.query';

import type { CreditBalanceResponse } from './subscription.types';

type SpendUnit = 'message' | 'run' | 'meeting' | 'use';

type Spend = { credits: number; count: number };

type BreakdownChild = Spend & { key: string; label: string; unit: SpendUnit };

type FeatureMeta = {
  label: string;
  unit: SpendUnit;
  colorClassName: string;
  // Shown only once used, for a feature that not every user has
  hiddenWhenUnused?: boolean;
};

type BreakdownRow = BreakdownChild &
  FeatureMeta & { children: BreakdownChild[] };

const FEATURE_META: Record<string, FeatureMeta> = {
  chat: {
    label: 'Chat',
    unit: 'message',
    colorClassName: 'bg-[var(--blue-9)]',
  },
  routine_run: {
    label: 'Routines',
    unit: 'run',
    colorClassName: 'bg-[var(--crimson-9)]',
  },
  meeting_prep: {
    label: 'Meeting prep',
    unit: 'meeting',
    colorClassName: 'bg-[var(--violet-9)]',
  },
  meeting_assist: {
    label: 'Meeting Assist',
    unit: 'meeting',
    colorClassName: 'bg-[var(--amber-9)]',
    hiddenWhenUnused: true,
  },
};

const OTHER_META: FeatureMeta = {
  label: 'Other',
  unit: 'use',
  colorClassName: 'bg-gray-8',
  hiddenWhenUnused: true,
};

const ROUTINE_FEATURE_KEYS = new Set([
  'routine_run',
  'routine_run_lite',
  'routine_run_max',
  'routine_run_ultra',
  'routine_run_god',
]);

const MODE_LABELS: Record<string, string> = {
  quick: 'Quick',
  default: 'Default',
  max: 'Max',
  ultra: 'Ultra',
  god: 'God',
  argon: 'Argon',
  other: 'Other',
};

const sumSpend = (items: Spend[]): Spend =>
  items.reduce(
    (acc, item) => ({
      credits: acc.credits + item.credits,
      count: acc.count + item.count,
    }),
    { credits: 0, count: 0 }
  );

const isRoutineSpend = (item: { feature_key: string }) =>
  ROUTINE_FEATURE_KEYS.has(item.feature_key);

const buildChildren = (
  featureKey: string,
  { spend_by_routine, spend_by_mode }: CreditBalanceResponse
): BreakdownChild[] => {
  if (featureKey === 'chat') {
    return (spend_by_mode ?? []).map((mode) => ({
      key: mode.mode,
      label: `${MODE_LABELS[mode.mode] ?? 'Other'} mode`,
      unit: 'message',
      credits: mode.credits,
      count: mode.count,
    }));
  }
  if (featureKey === 'routine_run') {
    return spend_by_routine.map((routine) => ({
      key: routine.routine_id ?? routine.name,
      label: routine.name,
      unit: 'run',
      credits: routine.credits,
      count: routine.count,
    }));
  }
  return [];
};

const buildRows = (data: CreditBalanceResponse): BreakdownRow[] => {
  const featureRows = Object.entries(FEATURE_META).map(([key, meta]) => {
    const items = data.spend_by_feature.filter((item) =>
      key === 'routine_run' ? isRoutineSpend(item) : item.feature_key === key
    );
    return {
      key,
      ...meta,
      ...sumSpend(items),
      children: buildChildren(key, data),
    };
  });
  const otherSpend = sumSpend(
    data.spend_by_feature.filter(
      (item) => !isRoutineSpend(item) && !FEATURE_META[item.feature_key]
    )
  );
  return (
    [
      ...featureRows,
      { key: 'other', ...OTHER_META, ...otherSpend, children: [] },
    ]
      // A feature can be used without consuming credits, when each use rounds to zero
      .filter(
        (row) => !row.hiddenWhenUnused || row.credits > 0 || row.count > 0
      )
      .sort((a, b) => b.credits - a.credits)
  );
};

const formatCount = (count: number, unit: SpendUnit) =>
  `${count.toLocaleString()} ${unit}${count === 1 ? '' : 's'}`;

const Swatch = ({ className }: { className: string }) => (
  <span className={cn('size-2 shrink-0 rounded-sm', className)} />
);

// A share that rounds to zero but used credits is shown as under a percent, so it does not read as free
const formatPct = (pct: number, credits: number) =>
  pct === 0 && credits > 0 ? '<1%' : `${pct}%`;

// Each feature's whole percent, with the points rounding loses given to the largest remainders so the features add
// up to 100
const wholePercents = (credits: number[], total: number): number[] => {
  if (total === 0) return credits.map(() => 0);
  const exact = credits.map((value) => (100 * value) / total);
  const whole = exact.map(Math.floor);
  const left = 100 - whole.reduce((sum, value) => sum + value, 0);
  exact
    .map((value, index) => ({ index, remainder: value - whole[index] }))
    .sort((a, b) => b.remainder - a.remainder)
    .slice(0, left)
    .forEach(({ index }) => {
      whole[index] += 1;
    });
  return whole;
};

const BreakdownLine = ({
  label,
  pct,
  credits,
  count,
  unit,
  swatchClassName,
  muted = false,
}: {
  label: string;
  pct: number;
  credits: number;
  count: number;
  unit: SpendUnit;
  swatchClassName?: string;
  muted?: boolean;
}) => (
  <Grid
    className={cn(
      'grid-cols-[1fr_3.5rem_7.5rem] items-center gap-3',
      muted ? 'text-xs' : 'text-sm'
    )}
  >
    <XStack className="min-w-0 items-center gap-2">
      {swatchClassName && <Swatch className={swatchClassName} />}
      <span
        className={cn(
          'truncate',
          muted ? 'text-foreground-muted' : 'text-foreground'
        )}
      >
        {label}
      </span>
    </XStack>
    <span
      className={cn(
        'text-right tabular-nums',
        muted ? 'text-foreground-muted' : 'text-foreground'
      )}
    >
      {formatPct(pct, credits)}
    </span>
    <span className="text-right tabular-nums text-foreground-muted">
      {formatCount(count, unit)}
    </span>
  </Grid>
);

type Segment = BreakdownRow & { pct: number; share: number };

type HoverProps = {
  hovered: string | null;
  onHover: (key: string | null) => void;
};

const StackedBar = ({
  segments,
  hovered,
  onHover,
}: { segments: Segment[] } & HoverProps) => (
  <XStack
    role="img"
    aria-label={`Usage by feature: ${segments
      .map(
        (segment) =>
          `${segment.label} ${formatPct(segment.pct, segment.credits)}`
      )
      .join(', ')}`}
    className="h-2 w-full gap-0.5 overflow-hidden rounded-full bg-grayA-3"
  >
    {segments
      .filter((segment) => segment.share > 0)
      .map((segment) => (
        <Tooltip
          key={segment.key}
          label={`${segment.label} · ${segment.pct}% · ${formatCount(segment.count, segment.unit)}`}
          delayDuration={0}
        >
          <div
            className={cn(
              'h-full transition-opacity',
              segment.colorClassName,
              hovered && hovered !== segment.key && 'opacity-40'
            )}
            style={{ width: `${segment.share * 100}%` }}
            onMouseEnter={() => onHover(segment.key)}
            onMouseLeave={() => onHover(null)}
          />
        </Tooltip>
      ))}
  </XStack>
);

const UsageBreakdownSkeleton = () => (
  <YStack className="gap-5">
    <Skeleton className="h-2 w-full rounded-full" />
    {Array.from({ length: 3 }).map((_, i) => (
      <XStack key={i} className="justify-between">
        <Skeleton className="h-3.5 w-24" />
        <Skeleton className="h-3.5 w-32" />
      </XStack>
    ))}
  </YStack>
);

export const UsageBreakdown = () => {
  const { data } = useCreditBalanceQuery();
  const [hovered, setHovered] = useState<string | null>(null);

  if (!data) return <UsageBreakdownSkeleton />;

  const rows = buildRows(data);
  const total = rows.reduce((sum, row) => sum + row.credits, 0);
  const pctOf = (credits: number) =>
    total > 0 ? Math.round((100 * credits) / total) : 0;

  // A used feature can have consumed no credits, when each use rounds to zero
  if (rows.every((row) => row.credits === 0 && row.count === 0)) {
    return (
      <span className="text-xs text-foreground-muted">
        Nothing yet. Chats, routines and meeting prep will show up here as you
        use them.
      </span>
    );
  }

  const percents = wholePercents(
    rows.map((row) => row.credits),
    total
  );
  const segments: Segment[] = rows.map((row, index) => ({
    ...row,
    pct: percents[index],
    share: row.credits / total,
  }));

  return (
    <YStack className="gap-5">
      <StackedBar segments={segments} hovered={hovered} onHover={setHovered} />
      <YStack className="divide-y divide-border">
        {segments.map((segment) => (
          <YStack
            key={segment.key}
            className={cn(
              'gap-1.5 py-4 transition-opacity first:pt-0 last:pb-0',
              hovered && hovered !== segment.key && 'opacity-40'
            )}
            onMouseEnter={() => setHovered(segment.key)}
            onMouseLeave={() => setHovered(null)}
          >
            <BreakdownLine
              label={segment.label}
              pct={segment.pct}
              credits={segment.credits}
              count={segment.count}
              unit={segment.unit}
              swatchClassName={segment.colorClassName}
            />
            {segment.children.length > 0 && (
              <YStack className="ml-1 mt-1 gap-1.5 border-l border-border pl-3">
                {segment.children.map((child) => (
                  <BreakdownLine
                    key={child.key}
                    label={child.label}
                    pct={pctOf(child.credits)}
                    credits={child.credits}
                    count={child.count}
                    unit={child.unit}
                    muted
                  />
                ))}
              </YStack>
            )}
          </YStack>
        ))}
      </YStack>
    </YStack>
  );
};
